// React dependencies
import React, { useEffect, useRef, useState } from 'react';

// External dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

// Internal dependencies
import { IsEmpty } from '../../../../../common/checks';
import MMap from '../../../../../components/MMap';
import Icons from '../../../../../components/Common/Icons';

const MapLocationSelect = (props) => {
  const mapRef = useRef(null);
  const mapsApiRef = useRef(null);
  const listenerMapClickRef = useRef(null);
  const zoomListenerRef = useRef(null);

  const selectedLocationRef = useRef(null);
  const [markersData, setMarkersData] = useState([]);

  /**
   *
   * @param {*} coord { lat, lng }
   * @returns
   */
  const panToCoord = (coord) => {
    try {
      if (IsEmpty(coord) || IsEmpty(mapRef.current)) {
        return;
      }

      mapRef.current.panTo(coord);

      const currentZoom = mapRef.current.getZoom() || 0;
      if (currentZoom !== props.zoomLevel) {
        mapRef.current.setZoom(props.zoomLevel);
        return;
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const removeZoomListener = () => {
    zoomListenerRef.current.remove();
    zoomListenerRef.current = null;
  };

  const adjustMapViewport = (currentCoord, staticMarkers) => {
    const map = mapRef.current;
    const maps = mapsApiRef.current;

    if (IsEmpty(map) || IsEmpty(maps)) {
      return;
    }

    const hasCurrent = !IsEmpty(currentCoord);
    const hasStatic = !IsEmpty(staticMarkers) && staticMarkers.length > 0;

    try {
      if (hasCurrent && hasStatic) {
        const bounds = new maps.LatLngBounds();

        bounds.extend(new maps.LatLng(currentCoord.lat, currentCoord.lng));

        staticMarkers.forEach((marker) => {
          if (!IsEmpty(marker.latitude) && !IsEmpty(marker.longitude)) {
            bounds.extend(new maps.LatLng(marker.latitude, marker.longitude));
          }
        });

        map.fitBounds(bounds, 50);

        zoomListenerRef.current = maps.event.addListener(map, 'idle', () => {
          if (map.getZoom() > props.zoomLevel) {
            map.setZoom(props.zoomLevel);
          }
          if (zoomListenerRef.current) {
            removeZoomListener();
          }
        });
      } else if (hasCurrent) {
        panToCoord(currentCoord);
      } else if (hasStatic) {
        panToCoord({ lat: staticMarkers[0].latitude, lng: staticMarkers[0].longitude });
      } else if (!IsEmpty(props.mapCenterCoord)) {
        panToCoord(props.mapCenterCoord);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    const { currentCoord, staticMarkersData } = props;

    selectedLocationRef.current = currentCoord;

    const markers = IsEmpty(currentCoord) ? [] : [{ latitude: currentCoord?.lat, longitude: currentCoord.lng }];
    setMarkersData(markers);

    adjustMapViewport(currentCoord, staticMarkersData);
  }, [props.currentCoord, props.staticMarkersData]);

  const onMapClick = (event) => {
    try {
      if (props.disabled) {
        return;
      }
      if (props.displayOnly) {
        props.onClick();
        return;
      }
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      const newPosition = {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
      };
      selectedLocationRef.current = newPosition;

      setMarkersData([{
        latitude:  newPosition.lat,
        longitude: newPosition.lng,
        isCurrent: true,
      }]);
      panToCoord(newPosition);

      props.onChange(newPosition);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const onMapLoaded = ({ map, maps }) => {
    mapRef.current = map;
    mapsApiRef.current = maps;
    if (map && maps) {
      listenerMapClickRef.current = mapRef.current.addListener('click', onMapClick);

      adjustMapViewport(selectedLocationRef.current, props.staticMarkersData);
    }
  };

  useEffect(() => () => {
    if (listenerMapClickRef.current) {
      listenerMapClickRef.current.remove();
    }
    if (zoomListenerRef.current) {
      removeZoomListener();
    }
  }, []);

  const onMarkerClick = (e) => {
    if (!IsEmpty(e)) {
      e.stopPropagation();
    }
    if (props.allowMarkerRemoveOnClick) {
      setMarkersData([]);
      props.onChange(null);
    }
  };

  return (
    <div
      className={`w-100 d-flex flex-column position-relative ${props.className}`}
      style={{
        minHeight: '15dvh',
        height:    props.height || '15dvh',
      }}
    >
      {
        props.showAlert && (
          <div
            className="position-absolute mt-2 w-100 mx-auto"
            style={{
              zIndex:    10,
              maxWidth:  320,
              left:      '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {props.alertComponent()}
          </div>
        )
      }
      <MMap
        GOOGLE_MAP_API_KEY={props.GOOGLE_MAP_API_KEY}
        onMapLoaded={onMapLoaded}
        markersData={markersData}
        markerIcon={props.markerIcon}
        center={props.mapCenterCoord}
        staticMarkersData={props.staticMarkersData || []}
        staticMarkerIcon={props.staticMarkerIcon}
        showGPSButton={props.showGPSButton}
        disabled={props.disabled || props.displayOnly}
        onMarkerClick={onMarkerClick}
      />
    </div>
  );
};

MapLocationSelect.propTypes = {
  allowMarkerRemoveOnClick: PropTypes.bool,

  className:    PropTypes.string,
  currentCoord: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  disabled:    PropTypes.bool,
  displayOnly: PropTypes.bool,
  height:      PropTypes.oneOf([PropTypes.number, PropTypes.string]),

  GOOGLE_MAP_API_KEY: PropTypes.string.isRequired,

  onChange: PropTypes.func,
  onClick:  PropTypes.func,

  alertComponent: PropTypes.func,
  showAlert:      PropTypes.bool,

  mapCenterCoord: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  markerIcon: PropTypes.func,

  staticMarkersData: PropTypes.arrayOf(PropTypes.shape({
    latitude:  PropTypes.number,
    longitude: PropTypes.number,
  })),
  staticMarkerIcon: PropTypes.func,
  showGPSButton:    PropTypes.bool,

  zoomLevel: PropTypes.number,
};

MapLocationSelect.defaultProps = {
  allowMarkerRemoveOnClick: false,

  className:    '',
  currentCoord: null,
  disabled:     false,
  displayOnly:  false,
  height:       null,

  onChange: () => {},
  onClick:  () => {},

  alertComponent: undefined,
  showAlert:      false,
  mapCenterCoord: null,
  markerIcon:     Icons.PhotoLocation,

  showGPSButton:     false,
  staticMarkersData: [],
  staticMarkerIcon:  null,

  zoomLevel: 17,
};

export default MapLocationSelect;
